import { PPQN } from "@opendaw/lib-dsp";
import { DefaultObservableValue, Notifier, Option, safeExecute, Terminator, UUID } from "@opendaw/lib-std";
import { AudioClipBox } from "@opendaw/studio-boxes";
import { Propagation } from "@opendaw/lib-box";
import { TrackBoxAdapter } from "../TrackBoxAdapter";
import { AudioFileBoxAdapter } from "../../audio/AudioFileBoxAdapter";
export class AudioClipBoxAdapter {
    type = "audio-clip";
    #terminator = new Terminator();
    #context;
    #box;
    #selectedValue;
    #changeNotifier;
    #isConstructing; // Prevents stack overflow due to infinite adapter queries
    #fileAdapter = Option.None;
    #fileSubscription = Option.None;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#isConstructing = true;
        this.#selectedValue = this.#terminator.own(new DefaultObservableValue(false));
        this.#changeNotifier = this.#terminator.own(new Notifier());
        this.#terminator.ownAll(this.#box.pointerHub.subscribe({
            onAdded: () => this.#dispatchChange(),
            onRemoved: () => this.#dispatchChange()
        }), this.#box.file.catchupAndSubscribe((pointerField) => {
            this.#fileAdapter = pointerField.targetVertex
                .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, AudioFileBoxAdapter));
            this.#fileSubscription.ifSome(subscription => subscription.terminate());
            this.#fileSubscription = this.#fileAdapter.map(adapter => adapter.getOrCreateLoader().subscribe(() => this.#dispatchChange()));
        }), this.#box.subscribe(Propagation.Children, (_update) => this.#dispatchChange()), {
            terminate: () => {
                this.#fileSubscription.ifSome(subscription => subscription.terminate());
                this.#fileSubscription = Option.None;
            }
        });
        this.#isConstructing = false;
    }
    catchupAndSubscribeSelected(observer) {
        return this.#selectedValue.catchupAndSubscribe(observer);
    }
    subscribeChange(observer) { return this.#changeNotifier.subscribe(observer); }
    accept(visitor) { return safeExecute(visitor.visitAudioClipBoxAdapter, this); }
    consolidate() { }
    clone(_mirrored) {
        AudioClipBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.index.setValue(this.indexField.getValue());
            box.gain.setValue(this.gain);
            box.label.setValue(this.label);
            box.hue.setValue(this.hue);
            box.duration.setValue(this.duration);
            box.mute.setValue(this.mute);
            box.clips.refer(this.#box.clips.targetVertex.unwrap());
            box.file.refer(this.#box.file.targetVertex.unwrap());
        });
    }
    onSelected() { this.#selectedValue.setValue(true); }
    onDeselected() { this.#selectedValue.setValue(false); }
    get isSelected() { return this.#selectedValue.getValue(); }
    terminate() { this.#terminator.terminate(); }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get indexField() { return this.#box.index; }
    get duration() { return this.#box.duration.getValue(); }
    get mute() { return this.#box.mute.getValue(); }
    get hue() { return this.#box.hue.getValue(); }
    get gain() { return this.#box.gain.getValue(); }
    get file() { return this.#fileAdapter.unwrap("Cannot access file."); }
    get hasCollection() { return !this.optCollection.isEmpty(); }
    get optCollection() { return Option.None; }
    get label() { return this.#box.label.getValue(); }
    get trackBoxAdapter() {
        if (this.#isConstructing) {
            return Option.None;
        }
        return this.#box.clips.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, TrackBoxAdapter));
    }
    get isMirrowed() { return false; }
    get canMirror() { return false; }
    toString() { return `{AudioClipBoxAdapter ${UUID.toString(this.#box.address.uuid)} d: ${PPQN.toString(this.duration)}}`; }
    #dispatchChange() {
        this.#changeNotifier.notify();
        this.trackBoxAdapter.unwrapOrNull()?.clips?.dispatchChange();
    }
}
